import type {
  GuildMemberTitles,
  GuildRankTitle,
  GuildSpecialTitle,
} from "@/core/guild-members/titles.js"

import { getCsrfTokenFromDocumentCookie } from "./auth-api"

export type MemberTitlesInput = {
  rankTitle: GuildRankTitle | null
  specialTitles: GuildSpecialTitle[]
}

export class MemberTitlesApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message)
    this.name = "MemberTitlesApiError"
  }
}

const readError = async (
  response: Response,
  fallback: string,
): Promise<MemberTitlesApiError> => {
  try {
    const body = (await response.json()) as {
      error?: { code?: unknown; message?: unknown }
      message?: unknown
    }
    const message =
      typeof body.error?.message === "string"
        ? body.error.message
        : typeof body.message === "string"
          ? body.message
          : fallback
    const code =
      typeof body.error?.code === "string" ? body.error.code : undefined
    return new MemberTitlesApiError(message, response.status, code)
  } catch {
    return new MemberTitlesApiError(fallback, response.status)
  }
}

const jsonHeaders = (): Record<string, string> => {
  const csrfToken = getCsrfTokenFromDocumentCookie()
  if (!csrfToken) {
    throw new Error("Missing CSRF token")
  }
  return {
    "content-type": "application/json",
    "x-csrf-token": csrfToken,
  }
}

export const memberTitlesApi = {
  get: async (memberId: string): Promise<GuildMemberTitles> => {
    const response = await fetch(
      `/api/member-titles/${encodeURIComponent(memberId)}`,
      { method: "GET", credentials: "include" },
    )
    if (!response.ok) {
      throw await readError(response, "Failed to load member titles")
    }
    const payload = (await response.json()) as { item: GuildMemberTitles }
    return payload.item
  },
  update: async (
    memberId: string,
    input: MemberTitlesInput,
  ): Promise<GuildMemberTitles> => {
    const response = await fetch(
      `/api/member-titles/${encodeURIComponent(memberId)}`,
      {
        method: "PUT",
        credentials: "include",
        headers: jsonHeaders(),
        body: JSON.stringify(input),
      },
    )
    if (!response.ok) {
      throw await readError(response, "Failed to update member titles")
    }
    const payload = (await response.json()) as { item: GuildMemberTitles }
    return payload.item
  },
}
